import { useEffect, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

type TUseCalculatePosition = () => {
  t: TFunction<'translation'>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  toggle: () => void;
  isOpen: boolean;
  panelRef: React.RefObject<HTMLDivElement>;
  position: {
    top: number;
    left: number;
  };
  handleFirstInteraction: () => void;
  shouldShowHints: boolean;
  tabPanelsRef: React.RefObject<HTMLDivElement>;
  showBottomFade: boolean;
  selectedTab: number;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleTabSelect: (index: number) => void;
};

export const useCalculatePosition: TUseCalculatePosition = () => {
  const { t } = useTranslation();

  /* Показываем и скрываем плашку размытия в списке фильтров */
  const tabPanelsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [showBottomFade, setShowBottomFade] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
  });
  const [filterState, setFilterState] = useState({
    isFirstInteractionDone: false,
  });

  /** Переменная для отображения первичных подсказок */
  const shouldShowHints = isOpen && !filterState.isFirstInteractionDone;

  const checkScroll = (element: HTMLDivElement) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    const hasScroll = scrollHeight > clientHeight;

    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    setShowBottomFade(hasScroll && !isAtBottom);
  };

  /** Рассчитываем позициию панели относительно кнопки */
  const toggle = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    setPosition({
      top: rect.bottom + 8,
      left: rect.right,
    });

    setIsOpen((prev) => {
      const nextValue = !prev;

      if (nextValue) {
        setFilterState({
          isFirstInteractionDone: false,
        });
      }

      return nextValue;
    });
  };

  const handleFirstInteraction = () => {
    if (filterState.isFirstInteractionDone) return;

    setFilterState({
      isFirstInteractionDone: true,
    });
  };

  /** Сбрасываем скролл позицию при переключении табов */
  const handleTabSelect = (index: number) => {
    tabPanelsRef.current?.scrollTo({ top: 0 });
    setSelectedTab(index);
  };

  useEffect(() => {
    if (!tabPanelsRef.current) return;
    checkScroll(tabPanelsRef.current);
  }, [isOpen, selectedTab]);

  useEffect(() => {
    const element = tabPanelsRef.current;
    if (!element) return undefined;
    const handleScroll = () => {
      checkScroll(element);
    };
    element.addEventListener('scroll', handleScroll);
    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePosition = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      setPosition({
        top: rect.bottom + 8,
        left: rect.right,
      });
    };

    window.addEventListener('resize', handlePosition);
    window.addEventListener('scroll', handlePosition, true);

    return () => {
      window.removeEventListener('resize', handlePosition);
      window.removeEventListener('scroll', handlePosition, true);
    };
  }, [isOpen]);

  const isEventInsideElement = (
    event: Event,
    element: HTMLElement | null,
  ) => {
    if (!element) return false;
    const target = event.target as HTMLElement;
    return event.composedPath().includes(element) || target.closest('[data-filter-interactive]');
  };


  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      if (isEventInsideElement(event, panelRef.current)) {
        return;
      }

      toggle();
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, toggle]);

  return {
    t,
    buttonRef,
    toggle,
    isOpen,
    panelRef,
    position,
    handleFirstInteraction,
    shouldShowHints,
    tabPanelsRef,
    showBottomFade,
    selectedTab,
    setIsOpen,
    handleTabSelect,
  };
};
